'use client';

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { pagadorasService } from '@/lib/api/pagadorasService';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Toast, ToastType } from '@/components/ui/Toast';
import { Switch } from '@/components/ui/Switch'; // Assuming Switch is available or similar

// Icons
const PlusIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const TrashIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const EditIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const SaveIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
  </svg>
);

const SearchIcon = () => (
  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const XIcon = () => (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const PagadorasManagement: React.FC = () => {
  // Data State
  const [pagadoras, setPagadoras] = useState<Record<string, string[]>>({});
  const [originalPagadoras, setOriginalPagadoras] = useState<Record<string, string[]>>({});

  // UI State
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [viewMode, setViewMode] = useState<'visual' | 'json'>('visual');
  const [selectedPagadora, setSelectedPagadora] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Mutation State
  const [newSegmento, setNewSegmento] = useState('');
  const [newPagadoraName, setNewPagadoraName] = useState('');
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState('');
  const [renameValue, setRenameValue] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importJsonText, setImportJsonText] = useState('');
  const [importError, setImportError] = useState<string | null>(null);

  // Components State
  const [toast, setToast] = useState<{ isOpen: boolean; message: string; type: ToastType }>({
    isOpen: false,
    message: '',
    type: 'info',
  });

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => { },
  });

  // Derived State
  const pagadoraKeys = useMemo(() => {
    return Object.keys(pagadoras).sort((a, b) => a.localeCompare(b));
  }, [pagadoras]);

  const filteredKeys = useMemo(() => {
    if (!searchTerm) return pagadoraKeys;
    const lower = searchTerm.toLowerCase();
    return pagadoraKeys.filter(k => k.toLowerCase().includes(lower));
  }, [pagadoraKeys, searchTerm]);

  const hasUnsavedChanges = useMemo(() => {
    return JSON.stringify(pagadoras) !== JSON.stringify(originalPagadoras);
  }, [pagadoras, originalPagadoras]);

  const showToast = useCallback((message: string, type: ToastType) => {
    setToast({ isOpen: true, message, type });
  }, []);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await pagadorasService.get();
      if (res.data) {
        setPagadoras(res.data);
        setOriginalPagadoras(res.data);
      } else {
        showToast(res.error || 'Error al cargar Pagadoras', 'error');
      }
    } catch (e) {
      showToast('Error de conexión al cargar Pagadoras', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(async () => {
    setIsSaving(true);
    try {
      const res = await pagadorasService.update(pagadoras);
      if (res.data) {
        setPagadoras(res.data);
        setOriginalPagadoras(res.data);
        showToast('Cambios guardados exitosamente', 'success');
      } else {
        showToast(res.error || 'Error al guardar', 'error');
      }
    } catch (e) {
      showToast('Error al guardar cambios', 'error');
    } finally {
      setIsSaving(false);
    }
  }, [pagadoras, showToast]);

  // Logic Actions (useCallback para evitar re-renders de hijos)
  const handleAddSegmento = useCallback(() => {
    if (!selectedPagadora || !newSegmento.trim()) return;
    const trimmed = newSegmento.trim();
    const currentSegmentos = pagadoras[selectedPagadora] || [];
    if (currentSegmentos.includes(trimmed)) {
      showToast('El segmento de cobranza ya existe', 'warning');
      return;
    }
    setPagadoras(prev => ({ ...prev, [selectedPagadora]: [...currentSegmentos, trimmed] }));
    setNewSegmento('');
  }, [selectedPagadora, newSegmento, pagadoras, showToast]);

  const handleRemoveSegmento = useCallback((segmento: string) => {
    if (!selectedPagadora) return;
    setPagadoras(prev => ({
      ...prev,
      [selectedPagadora]: prev[selectedPagadora].filter(s => s !== segmento)
    }));
  }, [selectedPagadora]);

  const handleDeletePagadora = useCallback((key: string) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Eliminar Pagadora',
      message: `¿Estás seguro de eliminar "${key}"? Esta acción no se puede deshacer.`,
      onConfirm: () => {
        setPagadoras(prev => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
        if (selectedPagadora === key) setSelectedPagadora(null);
        setConfirmDialog(d => ({ ...d, isOpen: false }));
        showToast('Pagadora eliminada', 'info');
      }
    });
  }, [selectedPagadora, showToast]);

  const handleCreatePagadora = useCallback(() => {
    if (!newPagadoraName.trim()) return;
    const name = newPagadoraName.trim();
    if (pagadoras[name]) {
      showToast('Ya existe una pagadora con ese nombre', 'error');
      return;
    }
    setPagadoras(prev => ({ ...prev, [name]: [] }));
    setSelectedPagadora(name);
    setNewPagadoraName('');
    setIsCreateModalOpen(false);
    showToast('Pagadora creada', 'success');
  }, [newPagadoraName, pagadoras, showToast]);

  const handleImportJson = useCallback(() => {
    setImportError(null);
    let parsed: unknown;
    try {
      parsed = JSON.parse(importJsonText);
    } catch {
      setImportError('JSON inválido. Revisa que no falten comas o llaves.');
      return;
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      setImportError('El JSON debe ser un objeto (ej: { "Nombre Pagadora": ["segmento1", "segmento2"] }).');
      return;
    }
    const asRecord = parsed as Record<string, unknown>;
    const normalized: Record<string, string[]> = {};
    for (const [key, value] of Object.entries(asRecord)) {
      if (typeof key !== 'string') continue;
      if (Array.isArray(value)) {
        normalized[key] = value.filter((v): v is string => typeof v === 'string');
      } else {
        normalized[key] = [];
      }
    }
    setPagadoras(normalized);
    setOriginalPagadoras(normalized);
    setImportJsonText('');
    setIsImportModalOpen(false);
    setImportError(null);
    showToast(`Importadas ${Object.keys(normalized).length} pagadoras. Guardando...`, 'info');
    (async () => {
      setIsSaving(true);
      try {
        const res = await pagadorasService.update(normalized);
        if (res.data) {
          setPagadoras(res.data);
          setOriginalPagadoras(res.data);
          showToast('JSON importado y guardado correctamente', 'success');
        } else {
          showToast(res.error || 'Error al guardar', 'error');
        }
      } catch (e) {
        showToast('Error al guardar después de importar', 'error');
      } finally {
        setIsSaving(false);
      }
    })();
  }, [importJsonText, showToast]);

  const handleRenamePagadora = useCallback(() => {
    if (!renameValue.trim() || !renameTarget) return;
    const newName = renameValue.trim();
    if (newName === renameTarget) {
      setIsRenameModalOpen(false);
      return;
    }
    if (pagadoras[newName]) {
      showToast('Ya existe una pagadora con ese nombre', 'error');
      return;
    }
    const segmentos = pagadoras[renameTarget];
    setPagadoras(prev => {
      const next = { ...prev };
      delete next[renameTarget];
      next[newName] = segmentos;
      return next;
    });
    if (selectedPagadora === renameTarget) setSelectedPagadora(newName);
    setIsRenameModalOpen(false);
    showToast('Pagadora renombrada', 'success');
  }, [renameValue, renameTarget, pagadoras, selectedPagadora, showToast]);

  const handleJsonChange = useCallback((val: string) => {
    try {
      const parsed = JSON.parse(val);
      if (typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('Root must be object');
      setPagadoras(parsed);
    } catch {
      // Permitir JSON inválido mientras se escribe
    }
  }, []);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64 bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Pagadoras</h1>
          <p className="text-sm text-gray-500 mt-1">Configura las empresas pagadoras y sus segmentos de cobranza reconocidos.</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            className="border-amber-200 text-amber-700 hover:bg-amber-50"
            onClick={() => {
              setImportJsonText('');
              setImportError(null);
              setIsImportModalOpen(true);
            }}
          >
            Importar JSON
          </Button>
          <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg mr-2">
            <button
              onClick={() => setViewMode('visual')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${viewMode === 'visual' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              Visual
            </button>
            <button
              onClick={() => setViewMode('json')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${viewMode === 'json' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              JSON
            </button>
          </div>

          {hasUnsavedChanges && (
            <Button onClick={save} disabled={isSaving} className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm">
              <SaveIcon />
              {isSaving ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          )}
        </div>
      </div>

      {viewMode === 'visual' ? (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 h-[calc(100vh-250px)] min-h-[600px]">
          {/* Left Panel: List */}
          <div className="lg:col-span-4 bg-white rounded-xl shadow-sm border border-gray-100 flex flex-col overflow-hidden">
            <div className="p-4 border-b border-gray-100 space-y-3">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Buscar pagadora..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
                <div className="absolute left-3 top-2.5">
                  <SearchIcon />
                </div>
              </div>
              <Button
                variant="secondary"
                className="w-full justify-center text-blue-600 border-blue-200 hover:bg-blue-50"
                onClick={() => setIsCreateModalOpen(true)}
              >
                <PlusIcon /> Nueva Pagadora
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              {filteredKeys.length === 0 ? (
                <div className="p-8 text-center text-gray-400 text-sm">
                  No se encontraron resultados
                </div>
              ) : (
                filteredKeys.map(key => (
                  <div
                    key={key}
                    onClick={() => setSelectedPagadora(key)}
                    className={`group flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${selectedPagadora === key
                        ? 'bg-blue-50 text-blue-700 border-blue-100 border'
                        : 'hover:bg-gray-50 text-gray-700 border border-transparent'
                      }`}
                  >
                    <div className="truncate font-medium flex-1 mr-2">{key}</div>
                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
                        {pagadoras[key].length}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Right Panel: Detail */}
          <div className="lg:col-span-8 bg-white rounded-xl shadow-sm border border-gray-100 flex flex-col overflow-hidden">
            {selectedPagadora ? (
              <>
                <div className="p-6 border-b border-gray-100 flex justify-between items-start bg-gray-50/50">
                  <div>
                    <div className="flex items-center gap-3">
                      <h2 className="text-2xl font-bold text-gray-800">{selectedPagadora}</h2>
                      <button
                        onClick={() => {
                          setRenameTarget(selectedPagadora);
                          setRenameValue(selectedPagadora);
                          setIsRenameModalOpen(true);
                        }}
                        className="text-gray-400 hover:text-blue-600 p-1 rounded transition-colors"
                        title="Renombrar"
                      >
                        <EditIcon />
                      </button>
                    </div>
                    <p className="text-gray-500 text-sm mt-1">
                      Gestiona los segmentos de cobranza asociados a esta pagadora.
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    className="text-red-500 hover:bg-red-50 hover:text-red-700"
                    onClick={() => handleDeletePagadora(selectedPagadora)}
                  >
                    <TrashIcon /> Eliminar
                  </Button>
                </div>

                <div className="p-6 flex-1 overflow-y-auto">
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Agregar nuevo segmento de cobranza</label>
                    <div className="flex gap-2 max-w-md">
                      <Input
                        placeholder="Ej. NOMBRE S.A. DE C.V."
                        value={newSegmento}
                        onChange={e => setNewSegmento(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleAddSegmento()}
                      />
                      <Button onClick={handleAddSegmento} disabled={!newSegmento.trim()}>
                        <PlusIcon /> Agregar
                      </Button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      Segmentos de cobranza registrados ({pagadoras[selectedPagadora].length})
                    </label>
                    {pagadoras[selectedPagadora].length === 0 ? (
                      <div className="p-8 border-2 border-dashed border-gray-200 rounded-xl text-center text-gray-400 bg-gray-50">
                        No hay segmentos de cobranza registrados. Agrega uno arriba.
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {pagadoras[selectedPagadora].map((segmento, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700 border border-blue-100 group"
                          >
                            {segmento}
                            <button
                              onClick={() => handleRemoveSegmento(segmento)}
                              className="ml-2 -mr-1 p-0.5 rounded-full hover:bg-blue-200 text-blue-400 hover:text-blue-800 transition-colors"
                            >
                              <XIcon />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex flex-col justify-center items-center text-gray-400 p-8">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4 text-gray-300">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <p className="text-lg font-medium text-gray-500">Selecciona una pagadora</p>
                <p className="text-sm mt-1">O crea una nueva para comenzar</p>
              </div>
            )}
          </div>
        </div>
      ) : (
        // JSON MODE
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 h-[600px] flex flex-col">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Editor JSON Avanzado</h3>
          <textarea
            className="flex-1 font-mono text-sm border border-gray-300 rounded-lg p-4 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full resize-none"
            value={JSON.stringify(pagadoras, null, 2)}
            onChange={e => handleJsonChange(e.target.value)}
            spellCheck={false}
          />
        </div>
      )}

      {/* Modals */}
      <Modal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} title="Nueva Pagadora">
        <div className="space-y-4">
          <Input
            label="Nombre de la Pagadora"
            placeholder="Ej. GOBIERNO DEL ESTADO"
            value={newPagadoraName}
            onChange={e => setNewPagadoraName(e.target.value)}
            autoFocus
          />
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="ghost" onClick={() => setIsCreateModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreatePagadora} disabled={!newPagadoraName.trim()}>Crear</Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={isRenameModalOpen} onClose={() => setIsRenameModalOpen(false)} title="Renombrar Pagadora">
        <div className="space-y-4">
          <Input
            label="Nuevo Nombre"
            value={renameValue}
            onChange={e => setRenameValue(e.target.value)}
            autoFocus
          />
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="ghost" onClick={() => setIsRenameModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleRenamePagadora} disabled={!renameValue.trim() || renameValue === renameTarget}>Guardar</Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isImportModalOpen}
        onClose={() => {
          setIsImportModalOpen(false);
          setImportError(null);
        }}
        title="Importar Pagadoras desde JSON"
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            Pega aquí el JSON con el formato: nombre de pagadora → lista de segmentos de cobranza. Ejemplo: <code className="bg-gray-100 px-1 rounded">{'{"CIPRE HOLDING": ["segmento1"]}'}</code>
          </p>
          <textarea
            className="w-full h-64 font-mono text-sm border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y"
            placeholder='{"Nombre Pagadora": ["segmento1", "segmento2"], ...}'
            value={importJsonText}
            onChange={e => {
              setImportJsonText(e.target.value);
              setImportError(null);
            }}
            spellCheck={false}
          />
          {importError && (
            <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">{importError}</p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setIsImportModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleImportJson} disabled={!importJsonText.trim()}>
              Importar y guardar en BD
            </Button>
          </div>
        </div>
      </Modal>

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast(t => ({ ...t, isOpen: false }))}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onClose={() => setConfirmDialog(c => ({ ...c, isOpen: false }))}
      />
    </div>
  );
};

export default PagadorasManagement;
